import { Controller } from "@hotwired/stimulus";

const SVG_NS = "http://www.w3.org/2000/svg";
const MD_BREAKPOINT = 768;

// Overview map interactions. Click a product node to select it (ring + dim the rest) and load its
// detail panel into the pos-product-panel frame. The SVG connector overlay is kept for related
// nodes, though the product-first map only renders one column so nothing is drawn today.
//
// Each node carries: data-key="product:<id>", data-rel="..." (| separated), and data-summary.
export default class extends Controller {
  static targets = [
    "node",
    "canvas",
    "canvasWrap",
    "panelFrame",
    "layout",
    "detailHeader",
    "detailName",
    "detailType",
    "saveBar",
    "saveButton",
  ];
  // panelUrl: URL template for the product detail frame, with KEY where the selected node's key
  // goes. mapFrameId: the id of the map's own live frame, so onFrameLoad can tell it apart from the
  // nested panel / per-location price frames whose loads also bubble here.
  static values = { panelUrl: String, mapFrameId: String };

  connect() {
    this.selectedKey = null;
    this.related = new Set();
    // The panel frame arrives already rendered in its empty state, so record that as the key we
    // last asked for and the clear() below won't refetch it.
    this.panelKey = null;
    this.boundRedraw = this.scheduleRedraw.bind(this);
    this.boundOutsideClick = this.handleOutsideClick.bind(this);
    this.boundFrameLoad = this.onFrameLoad.bind(this);
    this.boundFrameMissing = this.onFrameMissing.bind(this);
    this.boundBeforeFetch = this.onBeforeFetchRequest.bind(this);
    this.boundPanelEdit = this.onPanelEdit.bind(this);
    this.boundSubmitEnd = this.onPanelSubmitEnd.bind(this);
    window.addEventListener("resize", this.boundRedraw);
    window.addEventListener("scroll", this.boundRedraw, true);
    document.addEventListener("click", this.boundOutsideClick);
    // Enable the save button the moment the panel form is edited (it starts disabled on each
    // selection). input + change so both text fields and the multiselects count.
    this.element.addEventListener("input", this.boundPanelEdit);
    this.element.addEventListener("change", this.boundPanelEdit);
    // The panel form saves over Turbo (no full-page reload), so flash the outcome on the save button.
    this.element.addEventListener("turbo:submit-end", this.boundSubmitEnd);
    // The map reloads inside the POS live frame on search/filter; this controller lives on the
    // outer element and survives the swap, so re-apply the searched selection then.
    this.element.addEventListener("turbo:frame-load", this.boundFrameLoad);
    this.element.addEventListener("turbo:frame-missing", this.boundFrameMissing);
    // The map frame carries the panel, so anything that reloads it replaces a dirty panel form; by
    // frame-load time the edits are gone. Intercept at the last cancelable point, the fetch.
    this.element.addEventListener("turbo:before-fetch-request", this.boundBeforeFetch);
    this.clear();
    this.selectMatchedNode();
  }

  disconnect() {
    window.removeEventListener("resize", this.boundRedraw);
    window.removeEventListener("scroll", this.boundRedraw, true);
    document.removeEventListener("click", this.boundOutsideClick);
    this.element.removeEventListener("turbo:frame-load", this.boundFrameLoad);
    this.element.removeEventListener("turbo:frame-missing", this.boundFrameMissing);
    this.element.removeEventListener("turbo:before-fetch-request", this.boundBeforeFetch);
    this.element.removeEventListener("input", this.boundPanelEdit);
    this.element.removeEventListener("change", this.boundPanelEdit);
    this.element.removeEventListener("turbo:submit-end", this.boundSubmitEnd);
    if (this.savedTimeout) clearTimeout(this.savedTimeout);
  }

  // Editing the product panel form enables its (page-level) Save button.
  onPanelEdit(event) {
    if (!this.hasSaveButtonTarget) return;
    if (!event.target.closest("#pos-product-panel-form")) return;
    // An edit during the "Saved!" flash wins over the flash's pending reset: settle the flash now,
    // or its timer would fire and re-disable a button that is guarding fresh edits — silently
    // stranding them, since panelDirty() reads the disabled flag.
    this.settleSavedFlash();
    this.saveButtonTarget.disabled = false;
  }

  // The panel form saved over Turbo. On success the frame has already re-rendered in its saved
  // state, so flash "Saved!" then settle back to the disabled resting button. On failure the panel
  // showed an inline alert and kept the form, so re-enable the button to allow a retry.
  onPanelSubmitEnd(event) {
    const form = event.target;
    if (!this.hasSaveButtonTarget || form?.id !== "pos-product-panel-form") return;

    if (event.detail?.success) {
      this.flashSaved();
    } else {
      // The form cleared its dirty guard on submit, before the outcome was known. The save failed and
      // the panel kept the operator's edits, so re-arm both guards for a retry: the page Save button
      // and the document-level unsaved-changes prompt that navigations (e.g. "Edit product") honour.
      this.saveButtonTarget.disabled = false;
      this.application
        .getControllerForElementAndIdentifier(form, "ui--unsaved-changes")
        ?.markDirty();
    }
  }

  flashSaved() {
    const button = this.saveButtonTarget;
    const label = button.dataset.saveLabel || button.textContent.trim();
    button.dataset.saveLabel = label;
    button.textContent = "Saved!";
    button.disabled = true;
    if (this.savedTimeout) clearTimeout(this.savedTimeout);
    this.savedTimeout = setTimeout(() => this.settleSavedFlash(), 1500);
  }

  // Restore the resting "Save changes" label and drop the pending flash timer. The button's
  // disabled state is left alone: the timer path runs with it already disabled, and the
  // edit-during-flash path (onPanelEdit) enables it right after.
  settleSavedFlash() {
    if (!this.savedTimeout) return;
    clearTimeout(this.savedTimeout);
    this.savedTimeout = null;
    const button = this.saveButtonTarget;
    if (button.dataset.saveLabel) button.textContent = button.dataset.saveLabel;
  }

  // Turbo throws an uncaught "response did not contain the expected <turbo-frame>" when a frame
  // request comes back without that frame — a full-screen error overlay in development. The panel
  // endpoint always answers inside its frame (including when Nexus fails), but anything that can put
  // a whole page in front of it — a session-timeout redirect, an error page raised above the
  // controller — would still trip it. Swallow it for the panel frame: keep what's up, and forget the
  // key so the next click retries.
  onFrameMissing(event) {
    if (this.hasPanelFrameTarget && event.target === this.panelFrameTarget) {
      event.preventDefault();
      this.panelKey = null;
    }
  }

  // Guard a dirty panel against map-frame reloads before Turbo swaps anything. Toolbar search /
  // filter submissions (forms targeting the map frame — the event fires on the form) get the same
  // discard confirm as card switches. A fetch firing on the frame element itself is either a
  // targeted link, whose click already resolved the dirty state in handleOutsideClick, or a
  // background reload_frame stream from Mo's out-of-band edits — skip that one outright while
  // dirty rather than popping a confirm the operator didn't cause; a later reload catches the map
  // up. The panel form's own save targets its nested frame, so it never matches here.
  onBeforeFetchRequest(event) {
    if (event.target instanceof HTMLFormElement) {
      if (event.target.dataset.turboFrame !== this.mapFrameIdValue) return;
      if (!this.confirmDiscardIfDirty()) event.preventDefault();
    } else if (event.target.id === this.mapFrameIdValue && this.panelDirty()) {
      event.preventDefault();
    }
  }

  onFrameLoad(event) {
    // Only the map's own live frame re-applies the selection. turbo:frame-load bubbles, so the
    // nested panel frame (loaded by the selection itself) and the per-location price frame (reloaded
    // as the locations multiselect changes) would otherwise clear that selection. Match on the map
    // frame's id so any nested frame is ignored.
    if (event.target.id !== this.mapFrameIdValue) return;

    // The map's frame carries the panel, so a reload has already re-rendered it in its empty state.
    // Record that, and the clear() below won't refetch what's already there.
    this.panelKey = null;
    requestAnimationFrame(() => {
      this.clear();
      this.selectMatchedNode();
    });
  }

  // A search marks its hits with data-pos-match. Drop a single hit into the full selected state
  // (ring + related highlight); ring each of several hits so they all stand out.
  selectMatchedNode() {
    const matched = this.nodeTargets.filter((n) => n.dataset.posMatch === "true");
    if (matched.length === 1) {
      this.selectNode(matched[0]);
    } else {
      matched.forEach((n) => n.classList.add("ring-2", "ring-brand-500"));
    }
  }

  // Deselect when clicking anywhere outside the flow chart (the nodes live
  // inside canvasWrap, so node clicks never reach here as "outside").
  handleOutsideClick(event) {
    if (!this.selectedKey || !this.hasCanvasWrapTarget) return;
    // The product panel and tablet sit outside the canvas but ride on the current selection, so a
    // click in there would otherwise clear the very thing it's showing.
    if (event.target.closest?.("[data-pos-preview]")) return;
    // Opening or using Mo must not drop the selection — the whole point of "Add to Mo" / "Create a
    // promotion" is to carry the selected product into the chat. Ignore clicks on a Mo launcher
    // (ui--open-mo) or anywhere inside the Mo chat panel (admin--massimo-panel).
    if (event.target.closest?.("[data-controller~='ui--open-mo'], [data-controller~='admin--massimo-panel']")) return;
    if (this.canvasWrapTarget.contains(event.target)) return;
    // Clearing throws away the panel's unsaved edits, so guard it like the detail page guards
    // navigation.
    if (!this.confirmDiscardIfDirty()) {
      // If the discarded click was itself a navigation (a link outside the canvas), cancel it too —
      // otherwise the browser follows the link even though the operator chose to stay.
      event.preventDefault();
      return;
    }
    this.clear();
  }

  idList(value) {
    return (value || "").split("|").filter(Boolean);
  }

  // Explicit deselect (the panel's ✕ button) — the discoverable counterpart to clicking off the map.
  // Honours the same unsaved-changes guard a card switch or click-off does.
  deselect() {
    if (!this.confirmDiscardIfDirty()) return;
    this.clear();
  }

  select(event) {
    const key = event.currentTarget.dataset.key;
    // Switching to a different product replaces the panel and loses its unsaved edits — same as
    // navigating away from the detail page, so prompt the same way before doing it.
    if (key !== this.selectedKey && !this.confirmDiscardIfDirty()) return;
    this.selectNode(event.currentTarget);
    // The detail area (spanning header + panel) sits at the top of the section, and selecting
    // condenses the list to a thumbnail rail — so clicking a product low in a long, scrolled list
    // opens the panel above the fold. Scroll it back into view. Only from this user-driven select,
    // not the programmatic restore on connect/frame-load (which would yank the page on every reload).
    this.revealDetail();
  }

  // Bring the detail header (topmost element of the detail area) into view after a selection, so the
  // panel that opens below it is actually visible. A no-op when it's already comfortably on screen,
  // so clicking a product near the top doesn't cause a pointless jump.
  revealDetail() {
    if (!this.hasDetailHeaderTarget) return;
    const top = this.detailHeaderTarget.getBoundingClientRect().top;
    if (top >= 0 && top < window.innerHeight / 2) return;
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    this.detailHeaderTarget.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "start" });
  }

  // The panel form is dirty exactly when its Save button has been enabled (see onPanelEdit).
  panelDirty() {
    return this.hasSaveButtonTarget && !this.saveButtonTarget.disabled;
  }

  // Mirror the ui--unsaved-changes controller's confirm so a discard on the Overview reads the same
  // as one on the detail page.
  confirmDiscardIfDirty() {
    if (!this.panelDirty()) return true;

    return window.confirm("You have unsaved changes. Are you sure you want to leave?");
  }

  selectNode(node) {
    // Whether this is a switch to a different product (vs. reselecting the already-open one).
    const switched = node.dataset.key !== this.selectedKey;
    this.selectedKey = node.dataset.key;
    this.related = new Set(this.idList(node.dataset.rel));
    this.related.add(this.selectedKey);

    this.nodeTargets.forEach((n) => {
      const isSelected = n.dataset.key === this.selectedKey;
      const isRelated = this.related.has(n.dataset.key);
      n.classList.toggle("opacity-40", !isRelated);
      // Thin (1px) ring — brand blue for the selected card, lighter for related ones.
      n.classList.toggle("ring-1", isRelated);
      n.classList.toggle("ring-brand-500", isSelected);
      n.classList.toggle("shadow-md", isSelected);
      n.classList.toggle("ring-brand-300", isRelated && !isSelected);
      // Selected (and related) get a light brand-blue fill; the thin blue ring reads as "clicked".
      // Toggle the resting white/grey-hover off on those so the fill wins.
      n.classList.toggle("bg-brand-50", isRelated);
      n.classList.toggle("bg-white", !isRelated);
      n.classList.toggle("hover:bg-gray-50", !isRelated);
    });

    this.drawConnectors();
    this.updatePanel(this.selectedKey);
    // The page-wide save bar only makes sense once a product's editable panel is on the page. Its
    // button starts disabled when switching to a product — editing the panel form re-enables it
    // (onPanelEdit). Reselecting the already-open product must NOT disable it, or a pending save of
    // its unsaved edits would be stranded until the operator makes another edit to re-enable it.
    if (this.hasSaveBarTarget) this.saveBarTarget.hidden = false;
    // Open the detail arrangement: the layout condenses the product list to a thumbnail rail (CSS,
    // via the pos-detail-open ancestor) and the panel column slides in between the list and tablet.
    this.setDetailOpen(true);
    // Fill the spanning header straight from the node so the name/type show immediately, before the
    // panel frame's slower fetch resolves.
    this.updateDetailHeader(node);
    if (switched && this.hasSaveButtonTarget) this.saveButtonTarget.disabled = true;
  }

  // The map's spanning product header: name + type pulled from the selected node, revealed by
  // swapping its collapsed max-height/opacity. clear() collapses it again.
  updateDetailHeader(node) {
    if (this.hasDetailNameTarget) this.detailNameTarget.textContent = node?.dataset.productName || "";
    if (this.hasDetailTypeTarget) this.detailTypeTarget.textContent = node?.dataset.productType || "";
    this.toggleDetailHeader(true);
  }

  toggleDetailHeader(open) {
    if (!this.hasDetailHeaderTarget) return;
    const header = this.detailHeaderTarget;
    header.classList.toggle("max-h-0", !open);
    header.classList.toggle("opacity-0", !open);
    header.classList.toggle("max-h-24", open);
    header.classList.toggle("opacity-100", open);
    // Collapsed the header is still in the DOM (max-height/opacity, not display:none), so keep it out
    // of the a11y tree and its ✕ un-focusable while there's no selection.
    header.inert = !open;
  }

  // Toggle the master-detail arrangement. The layout element's pos-detail-open class is all it takes:
  // in CSS it animates the grid's column widths open/closed (the panel track grows from 0), condenses
  // the tiles to a thumbnail rail, and the panel content eases in (animate-panel-in).
  setDetailOpen(open) {
    if (this.hasLayoutTarget) this.layoutTarget.classList.toggle("pos-detail-open", open);
  }

  // Point the product detail panel frame at the selection. Keyed on the last key we asked for, so
  // the clear() in connect()/turbo:frame-load doesn't refetch the empty state on every load.
  updatePanel(key) {
    if (!this.hasPanelFrameTarget || !this.hasPanelUrlValue) return;
    if (this.panelKey === key) return;

    this.panelKey = key;
    // Drop a grey skeleton into the frame so the panel column doesn't slide open empty (or keep the
    // previous product's contents) while Turbo fetches the new src. Turbo swaps in the real panel on
    // load. Only for a real selection — a keyless (idle/clear) update shouldn't flash a skeleton.
    if (key) this.panelFrameTarget.innerHTML = this.panelSkeleton();
    this.panelFrameTarget.src = this.panelUrlValue.replace("KEY", encodeURIComponent(key || ""));
  }

  // Grey placeholder mirroring the stacked sections of the loaded product panel (_product_panel).
  // Shown for the brief window between selecting a product and Turbo swapping in its detail frame.
  panelSkeleton() {
    return `
      <div data-pos-preview class="flex min-h-full flex-col gap-3" aria-hidden="true">
        ${[0, 1, 2]
          .map(
            () => `
          <div class="animate-pulse rounded-xl border border-gray-200 bg-white p-4">
            <div class="h-3 w-24 rounded bg-gray-200"></div>
            <div class="mt-3 h-9 rounded-lg bg-gray-100"></div>
          </div>`
          )
          .join("")}
      </div>
    `;
  }

  clear() {
    this.selectedKey = null;
    this.related = new Set();
    this.nodeTargets.forEach((n) => {
      n.classList.remove(
        "opacity-40",
        "ring-2",
        "ring-brand-500",
        "ring-1",
        "ring-brand-300",
        "bg-brand-50",
        "shadow-md"
      );
      // Restore the resting tint we toggled off on selection.
      n.classList.add("bg-white", "hover:bg-gray-50");
    });
    this.clearConnectors();
    this.updatePanel(null);
    this.setDetailOpen(false);
    this.toggleDetailHeader(false);
    if (this.hasSaveBarTarget) this.saveBarTarget.hidden = true;
    // The panel is empty now, so nothing is dirty: without this, a confirmed discard would leave
    // the button armed and a later fetch guard or card click would re-prompt over an empty panel.
    if (this.hasSaveButtonTarget) this.saveButtonTarget.disabled = true;
  }

  scheduleRedraw() {
    if (this.redrawFrame) cancelAnimationFrame(this.redrawFrame);
    this.redrawFrame = requestAnimationFrame(() => this.drawConnectors());
  }

  drawConnectors() {
    this.clearConnectors();
    if (!this.selectedKey || !this.hasCanvasTarget || window.innerWidth < MD_BREAKPOINT) return;

    const from = this.nodeFor(this.selectedKey);
    if (!from) return;
    const wrap = this.canvasWrapTarget.getBoundingClientRect();
    const fromRect = from.getBoundingClientRect();

    this.related.forEach((key) => {
      if (key === this.selectedKey) return;
      const to = this.nodeFor(key);
      if (!to) return;
      const toRect = to.getBoundingClientRect();
      const toRight = toRect.left >= fromRect.left;
      const x1 = (toRight ? fromRect.right : fromRect.left) - wrap.left;
      const y1 = fromRect.top + fromRect.height / 2 - wrap.top;
      const x2 = (toRight ? toRect.left : toRect.right) - wrap.left;
      const y2 = toRect.top + toRect.height / 2 - wrap.top;
      const c = Math.max(40, Math.abs(x2 - x1) / 2);

      const path = document.createElementNS(SVG_NS, "path");
      path.setAttribute(
        "d",
        `M ${x1} ${y1} C ${toRight ? x1 + c : x1 - c} ${y1}, ${toRight ? x2 - c : x2 + c} ${y2}, ${x2} ${y2}`
      );
      path.setAttribute("fill", "none");
      path.setAttribute("stroke", "#5C80E0");
      path.setAttribute("stroke-width", "2");
      path.setAttribute("marker-end", "url(#pos-arrow)");
      this.connectorGroup.appendChild(path);
    });
  }

  clearConnectors() {
    if (this.hasCanvasTarget) this.connectorGroup.replaceChildren();
  }

  get connectorGroup() {
    return this.canvasTarget.querySelector("[data-connectors]");
  }

  nodeFor(key) {
    return this.nodeTargets.find((n) => n.dataset.key === key);
  }
}
